import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { format, addDays } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BookingModal = ({ isOpen, onClose }) => {
  const { t, language, isRTL } = useLanguage();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  // Form state
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    notes: '',
  });

  const services = [
    { id: 'hair-styling', name_en: 'Hair Styling', name_ar: 'تصفيف الشعر' },
    { id: 'hair-washing', name_en: 'Hair Washing & Conditioning', name_ar: 'غسيل وترطيب الشعر' },
    { id: 'hair-treatment', name_en: 'Hair Treatments', name_ar: 'علاجات الشعر' },
    { id: 'professional-styling', name_en: 'Professional Styling', name_ar: 'تصفيف احترافي' },
    { id: 'makeup', name_en: 'Makeup Services', name_ar: 'خدمات المكياج' },
    { id: 'nail-care', name_en: 'Nail Care', name_ar: 'العناية بالأظافر' },
    { id: 'facial', name_en: 'Facial Treatments', name_ar: 'علاجات الوجه' },
    { id: 'spa', name_en: 'Spa & Relaxation', name_ar: 'سبا واسترخاء' },
  ];

  // Fetch available slots when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async (date) => {
    try {
      const response = await axios.get(`${API}/available-slots/${date}`);
      setAvailableSlots(response.data.available_slots);
    } catch (err) {
      console.error('Error fetching slots:', err);
      setAvailableSlots([]);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      const selectedServiceObj = services.find(s => s.id === selectedService);
      
      await axios.post(`${API}/appointments`, {
        customer_name: formData.name,
        customer_phone: formData.phone,
        customer_email: formData.email || null,
        service: language === 'ar' ? selectedServiceObj.name_ar : selectedServiceObj.name_en,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
        notes: formData.notes || null,
      });
      
      setSuccess(true);
    } catch (err) {
      console.error('Booking error:', err);
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedService('');
    setSelectedDate(null);
    setSelectedTime('');
    setFormData({ name: '', phone: '', email: '', notes: '' });
    setSuccess(false);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return selectedService !== '';
      case 2:
        return selectedDate !== null && selectedTime !== '';
      case 3:
        return formData.name.trim() !== '' && formData.phone.trim() !== '';
      default:
        return false;
    }
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl"
          data-testid="booking-modal"
        >
          {/* Header */}
          <div className="bg-[#F9F7F2] px-6 py-4 flex items-center justify-between">
            <h2 className="heading text-xl font-semibold text-[#4A4A4A]">
              {t.booking.title}
            </h2>
            <button
              data-testid="booking-close-btn"
              onClick={handleClose}
              className="text-[#7D7D7D] hover:text-[#4A4A4A] transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Bar */}
          {!success && (
            <div className="px-6 py-3 bg-[#F9F7F2]/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-[#7D7D7D]">
                  {t.booking.step} {step} {t.booking.of} 3
                </span>
              </div>
              <div className="h-2 bg-[#E5E5E5] rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(step / 3) * 100}%` }}
                  className="h-full bg-[#D4A39A] rounded-full"
                />
              </div>
            </div>
          )}

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {success ? (
              // Success Screen
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-8"
              >
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                  <Check className="w-10 h-10 text-green-500" />
                </div>
                <h3 className="heading text-2xl font-semibold text-[#4A4A4A] mb-3">
                  {t.booking.success}
                </h3>
                <p className="text-[#7D7D7D] mb-8">
                  {t.booking.successMessage}
                </p>
                <Button
                  data-testid="booking-done-btn"
                  onClick={handleClose}
                  className="btn-primary"
                >
                  {t.booking.close}
                </Button>
              </motion.div>
            ) : (
              <>
                {/* Step 1: Service Selection */}
                {step === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <h3 className="text-lg font-medium text-[#4A4A4A] mb-4">
                      {t.booking.selectService}
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {services.map((service) => (
                        <button
                          key={service.id}
                          data-testid={`service-option-${service.id}`}
                          onClick={() => setSelectedService(service.id)}
                          className={`p-4 rounded-xl border-2 text-start transition-all ${
                            selectedService === service.id
                              ? 'border-[#D4A39A] bg-[#D4A39A]/10'
                              : 'border-[#E5E5E5] hover:border-[#D4A39A]/50'
                          }`}
                        >
                          <span className="text-sm font-medium text-[#4A4A4A]">
                            {language === 'ar' ? service.name_ar : service.name_en}
                          </span>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Date & Time Selection */}
                {step === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <h3 className="text-lg font-medium text-[#4A4A4A] mb-4">
                      {t.booking.selectDate}
                    </h3>
                    
                    <div className="flex justify-center mb-6">
                      <Calendar
                        data-testid="booking-calendar"
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={isDateDisabled}
                        locale={language === 'ar' ? ar : enUS}
                        className="rounded-xl border"
                        fromDate={new Date()}
                        toDate={addDays(new Date(), 60)}
                      />
                    </div>

                    {selectedDate && (
                      <>
                        <h3 className="text-lg font-medium text-[#4A4A4A] mb-4">
                          {t.booking.selectTime}
                        </h3>
                        {availableSlots.length === 0 ? (
                          <p className="text-center text-[#7D7D7D]">
                            {t.booking.noSlotsAvailable}
                          </p>
                        ) : (
                          <div className="time-slots-grid">
                            {availableSlots.map((slot) => (
                              <button
                                key={slot}
                                data-testid={`time-slot-${slot}`}
                                onClick={() => setSelectedTime(slot)}
                                className={`py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                                  selectedTime === slot
                                    ? 'bg-[#D4A39A] text-white'
                                    : 'bg-[#F9F7F2] text-[#4A4A4A] hover:bg-[#D4A39A]/20'
                                }`}
                              >
                                {slot}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </motion.div>
                )}

                {/* Step 3: Personal Details */}
                {step === 3 && (
                  <motion.div
                    initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <h3 className="text-lg font-medium text-[#4A4A4A] mb-4">
                      {t.booking.yourDetails}
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="name" className="text-[#4A4A4A]">
                          {t.booking.name} *
                        </Label>
                        <Input
                          id="name"
                          data-testid="booking-name-input"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="mt-1"
                          placeholder={language === 'ar' ? 'أدخلي اسمك الكامل' : 'Enter your full name'}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="phone" className="text-[#4A4A4A]">
                          {t.booking.phone} *
                        </Label>
                        <Input
                          id="phone"
                          data-testid="booking-phone-input"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="mt-1"
                          placeholder="05X XXX XXXX"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="email" className="text-[#4A4A4A]">
                          {t.booking.email}
                        </Label>
                        <Input
                          id="email"
                          data-testid="booking-email-input"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="mt-1"
                          placeholder="example@email.com"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="notes" className="text-[#4A4A4A]">
                          {t.booking.notes}
                        </Label>
                        <Textarea
                          id="notes"
                          data-testid="booking-notes-input"
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          className="mt-1"
                          rows={3}
                          placeholder={language === 'ar' ? 'أي طلبات خاصة...' : 'Any special requests...'}
                        />
                      </div>
                    </div>

                    {error && (
                      <p className="text-red-500 text-sm mt-4">{error}</p>
                    )}
                  </motion.div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {!success && (
            <div className="px-6 py-4 border-t border-[#E5E5E5] flex items-center justify-between">
              {step > 1 ? (
                <Button
                  data-testid="booking-back-btn"
                  variant="ghost"
                  onClick={() => setStep(step - 1)}
                  className="flex items-center gap-2 text-[#7D7D7D]"
                >
                  {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                  {t.booking.back}
                </Button>
              ) : (
                <div />
              )}
              
              {step < 3 ? (
                <Button
                  data-testid="booking-next-btn"
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                  className="btn-primary flex items-center gap-2"
                >
                  {t.booking.next}
                  {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </Button>
              ) : (
                <Button
                  data-testid="booking-confirm-btn"
                  onClick={handleSubmit}
                  disabled={!canProceed() || loading}
                  className="btn-accent flex items-center gap-2"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t.booking.confirm}
                </Button>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
